import { useMemo, useState } from "react";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { MdDelete, MdEdit, MdMoreVert } from "react-icons/md";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import { getErrorMessage } from "../../utils/error";

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `₦${numeric.toLocaleString()}`;
};

const getStatusChipStyles = (isActive) =>
  isActive
    ? { bg: "#DCFCE7", color: "#166534" }
    : { bg: "#F1F5F9", color: "#64748B" };

export default function AddonsTable({
  addons,
  addonsLoading,
  addonsError,
  addonsErrorObj,
  addonsRefetch,
  page,
  limit,
  totalCount,
  onPageChange,
  onLimitChange,
  onEditAddon,
  onDeleteAddon,
  onToggleStatus,
  isTogglingStatus,
  isDeletingAddon,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAddon, setMenuAddon] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const tableRows = useMemo(() => addons || [], [addons]);

  const handleMenuOpen = (event, addon) => {
    setAnchorEl(event.currentTarget);
    setMenuAddon(addon);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAddon(null);
  };

  const handleEditAddon = () => {
    if (!menuAddon) return;
    onEditAddon(menuAddon);
    handleMenuClose();
  };

  const handleAskDelete = () => {
    if (!menuAddon) return;
    setDeleteTarget(menuAddon);
    handleMenuClose();
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    onDeleteAddon(deleteTarget);
    setDeleteTarget(null);
  };

  return addonsLoading ? (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        textAlign: "center",
      }}
    >
      <Typography className="text-slate-600">Loading add-ons...</Typography>
    </Paper>
  ) : addonsError ? (
    <ErrorRetry
      message={getErrorMessage(addonsErrorObj)}
      onRetry={() => addonsRefetch()}
    />
  ) : (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 980 }}>
            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Add-on
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Privileges
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Billing Methods
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Monthly
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Annual
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography className="text-slate-600 py-6 text-center">
                      No add-ons found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((addon) => {
                  const statusStyles = getStatusChipStyles(addon.isActive);

                  return (
                    <TableRow
                      key={addon._id || addon.id}
                      sx={{
                        "&:hover": {
                          bgcolor: "#F8FAFC",
                        },
                      }}
                    >
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography className="font-semibold text-slate-900">
                          {addon.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="text-slate-500 mt-1"
                        >
                          {addon.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Box className="flex flex-wrap gap-1">
                          {(addon.privileges || []).length > 0 ? (
                            addon.privileges.map((privilege) => (
                              <Chip
                                key={privilege}
                                label={privilege}
                                size="small"
                                sx={{
                                  bgcolor: "#DBEAFE",
                                  color: "#1D4ED8",
                                  fontWeight: 600,
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              className="text-slate-500"
                            >
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Box className="flex flex-wrap gap-1">
                          {(addon.allowedBillingMethods || []).length > 0 ? (
                            addon.allowedBillingMethods.map((method) => (
                              <Chip
                                key={method}
                                label={method}
                                size="small"
                                sx={{
                                  bgcolor: "#F3E8FF",
                                  color: "#7C3AED",
                                  fontWeight: 600,
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              className="text-slate-500"
                            >
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography className="text-slate-700">
                          {formatCurrency(addon.priceMonthly)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className="text-slate-700">
                          {formatCurrency(addon.priceAnnual)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box className="flex flex-col gap-1">
                          <Chip
                            label={addon.isActive ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              alignSelf: "flex-start",
                              bgcolor: statusStyles.bg,
                              color: statusStyles.color,
                              fontWeight: 600,
                            }}
                          />
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(addon.isActive)}
                                disabled={isTogglingStatus}
                                onChange={() =>
                                  onToggleStatus(addon, !addon.isActive)
                                }
                              />
                            }
                            label=""
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuOpen(event, addon)}
                        >
                          <MdMoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_event, newPage) => onPageChange(newPage)}
          rowsPerPage={limit}
          onRowsPerPageChange={(event) => {
            const newLimit = parseInt(event.target.value, 10);
            onLimitChange(newLimit);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: "10px", minWidth: 170 },
        }}
      >
        <MenuItem onClick={handleEditAddon}>
          <MdEdit className="mr-2" />
          Edit Add-on
        </MenuItem>
        {/* <MenuItem onClick={handleAskDelete} sx={{ color: "#DC2626" }}>
          <MdDelete className="mr-2" />
          Delete Add-on
        </MenuItem> */}
      </Menu>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: 360 } }}
      >
        <DialogTitle>Delete Add-on</DialogTitle>
        <DialogContent>
          <Typography className="text-slate-600">
            Delete <span className="font-semibold">{deleteTarget?.name}</span>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={isDeletingAddon}
            sx={{ textTransform: "none", fontWeight: 600, color: "#475569" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeletingAddon}
            variant="contained"
            color="error"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {isDeletingAddon ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
